import React, { useContext, useEffect, useMemo } from "react";
import FullTaskTopbar from "../../Components/Topbars/FullTaskTopbar";
import FullTaskLayout from "../../Layouts/FullTask";
import { ControlledPlayer } from "./Controlled-player";
import { Box, Paper, Stack, Button, CircularProgress, Popover, Typography, Chip } from "@mui/material";
import TimelineEditor from "./Timeline-editor";
import MediaLibrary from "./Media-library";
import { useSelector } from "react-redux";
import {
  GetShowLibrary,
  GetShowSequence,
  GetShowTimeline,
  GetSelectedAssetFile,
  GetFps,
  SetShowSequence,
  GetOrderedActionsList,
  GetSequenceDuration,
  GetSequenceName,
  SetFps,
  SetSequenceName,
  GetLoadedSequence,
  generateMetadata,
  GetTimelineActions,
  GetIsLoading,
  SetTimelineActions,
  SetIsLoading,
  SetLoadedSequence,
  InitState,
  GetHandleAudio,
} from "../../Redux/Reducers/redux-slice-timetool";
import { ControlledSequence } from "./Controlled-sequence";
import { useDispatch } from "react-redux";
import { ModalContext } from "../../Components/Modals";
import ModalTimetoolConfig, {
  ModalTimetoolConfigElementProps,
} from "../../Components/Modals/ModalTimetoolConfig/modal-timetool-config";
import { TimetoolFooter } from "./TimetoolFooter";
import { assetService, cacheService, mediaService } from "../../Core/Api/services";
import { UpdateMetadataFieldsRequest } from "../../Core/Api/Types/update-metadata-fields-request";
import { BaseMetadataType } from "../../Core/Api/Types/base-metadata-type";
import { CreateNewDbMetadataValue, GetMetadataByTagName } from "../../Core/Utils/asset-object-utils";
import useCustomSnackbar from "../../Utils/Hooks/useCustomSnackbar";
import {
  ModalTimetoolLoadSequence,
  ModalTimetoolLoadSequenceElementProps,
} from "../../Components/Modals/ModalTimetoolLoadSequence/modal-timetool-load-sequence";
import { useParams } from "react-router";
import { DbAssetObjectInfos } from "../../Core/Api/Types/db-asset-object-infos";
import ModalTimetoolGenerateCompilation, {
  ModalTimetoolGenerateCompilationElementProps,
} from "../../Components/Modals/ModalTimeToolCompilation/modal-timetool-generate-compilation";
import { IDbBaseMetadataValueInfos } from "../../Core/Api/Types/i-db-base-metadata-value-infos";
import { SequenceMetadataSegment } from "../../Core/Api/Types/sequence-metadata-segment";
import { v4 as uuidv4 } from "uuid";
import { UserHasLicence } from "../../Utils/helpers";
import { Licences } from "../../Core/Api/Types/licences";
import { routePaths } from "../../Router/router";
import QuestionMarkOutlined from "@mui/icons-material/QuestionMarkOutlined";
import { TimecodeToSec } from "../../Utils/Timecode/ReactPlayer";
import { CusTomTimelineRow } from "./custom-timeline-row";
import { AudioChannel } from "./custom-timeline-action";
import { useTranslation } from "react-i18next";

export const Timetool: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const selectedFileToPreview = useSelector(GetSelectedAssetFile);
  const showTimeline = useSelector(GetShowTimeline);
  const showSequence = useSelector(GetShowSequence);
  const showLibrary = useSelector(GetShowLibrary);
  const sequenceDuration = useSelector(GetSequenceDuration);
  const orderedActionsList = useSelector(GetOrderedActionsList);
  const timelineActions = useSelector(GetTimelineActions);
  const sequenceName = useSelector(GetSequenceName);
  const fps = useSelector(GetFps);
  const loadedSequence = useSelector(GetLoadedSequence);
  const isLoading = useSelector(GetIsLoading);
  const handleAudio = useSelector(GetHandleAudio);

  useEffect(() => {
    dispatch(SetShowSequence(true));
  }, [handleAudio]);

  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const enqueueCustomSnackbar = useCustomSnackbar();

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [loadingMessage, setLoadingMessage] = React.useState<string>("");
  const userHasCompilationLicense = useMemo(() => UserHasLicence(Licences.TimetoolCompilation), []);

  const MediaLibraryMemo = useMemo(() => <MediaLibrary />, [showLibrary]);

  const params = useParams();
  const id: number | undefined = params.id ? parseInt(params.id) : undefined;

  const handleLoadFromSegments = async (segments: SequenceMetadataSegment[]) => {
    setLoadingMessage(`loading sequence: ${segments.length} segments to load...`);
    const fps = segments[0].frameRate!;

    const objectIdList = segments
      .map((x) => x.objectId!)
      .filter((x, i) => segments.findIndex((n) => n.objectId === x) === i);
    const objectIdMap: {
      objectId: number;
      src: string;
      duration: number;
      audioMapping?: AudioChannel[] | null;
    }[] = [];
    for (let i = 0; i < objectIdList.length; i++) {
      const res = await assetService.GetObjectById(objectIdList[i]);

      const streamRes = await mediaService.GetStreamingSmilByObjectId(objectIdList[i], null);

      const frameRateMetaId = cacheService.GetMetadata().find((x) => x.tagName === "techFrameRate")?.id;
      const frameRateValue = res.graph?.metadata?.find((x) => x.id === frameRateMetaId)?.value || 24;
      const durationMetaId = cacheService.GetMetadata().find((x) => x.tagName === "durationSMPTE")?.id;
      const durationValue = TimecodeToSec(
        res.graph?.metadata?.find((x) => x.id === durationMetaId)?.value || "00:00:00:00",
        frameRateValue
      );

      let metadata = res.graph && GetMetadataByTagName(res.graph, "audioChannels");
      let audioChannels: any[] = metadata && metadata.value ? JSON.parse(JSON.stringify(metadata?.value)) : [];
      const rowSrc = streamRes.smilFullUrl;

      if (!rowSrc || !durationValue) continue;

      objectIdMap.push({
        objectId: objectIdList[i],
        src: rowSrc,
        duration: durationValue,
        audioMapping: audioChannels,
      });
    }

    let newTimelineActions: CusTomTimelineRow[] = [];
    const mappedObjectIds = objectIdMap.map((x) => x.objectId);
    const usedSegments = segments.filter((x) => x.objectId && mappedObjectIds.includes(x.objectId));

    const maxTrackNumber = Math.max(...usedSegments.map((s) => s.track!));
    for (let i = 0; i < maxTrackNumber + 1; i++) {
      newTimelineActions.push({ id: i.toString(), actions: [] });
    }

    for (let i = 0; i < segments.length; i++) {
      const actionId = uuidv4();
      const assetAudioMapping = objectIdMap.find((x) => x.objectId === segments[i].objectId!)!.audioMapping;
      let segment = {
        data: {
          src: objectIdMap.find((x) => x.objectId === segments[i].objectId!)!.src,
          name: segments[i].assetName ?? "",
          fps: segments[i].frameRate ?? 24,
          offSet: segments[i].frameIn ?? 0,
          duration: objectIdMap.find((x) => x.objectId === segments[i].objectId!)!.duration,
          id: (segments[i].objectId ?? 0).toString(),
          actionId: actionId,
          titleId: segments[i].titleId,
          audioMapping: assetAudioMapping
            ? segments[i].audioChannels
              ? segments[i].audioChannels
                  ?.filter((x) => assetAudioMapping[x.inputIndex])
                  .map((x) => assetAudioMapping[x.inputIndex])
              : assetAudioMapping
            : null,
        },
        start: segments[i].frameStart! / fps,
        end: (segments[i].frameStart! + (segments[i].frameOut! - segments[i].frameIn!)) / fps,
        id: actionId,
        effectId: segments[i].assetName ?? "",
      };
      if (segment.end <= segment.start) continue;

      newTimelineActions[segments[i].track!].actions.push(segment);
    }
    dispatch(SetTimelineActions(newTimelineActions));
    dispatch(SetFps(fps));
    setLoadingMessage("");
    dispatch(SetIsLoading(false));
  };

  const handlLoadFromMeta = (sequenceSegmentMetadata: IDbBaseMetadataValueInfos) => {
    handleLoadFromSegments(sequenceSegmentMetadata.value);
  };

  const handleLoadFromAsset = (sequenceToLoad: DbAssetObjectInfos) => {
    dispatch(SetLoadedSequence({ ...sequenceToLoad }));
    dispatch(SetIsLoading(true));
    const sequenceSegmentMetadataId = cacheService.GetMetadata().find((x) => x.tagName === "sequenceSegments")?.id;
    const sequenceSegmentMetadata = sequenceToLoad.metadata?.find((x) => x.id === sequenceSegmentMetadataId);
    if (!sequenceSegmentMetadata?.value) return;

    handlLoadFromMeta(sequenceSegmentMetadata);
  };

  useEffect(() => {
    if (id !== undefined) {
      dispatch(SetIsLoading(true));
      assetService.GetObjectById(id).then((res) => {
        if (!res.graph) return;
        const sequenceToLoad: DbAssetObjectInfos = res.graph;
        handleLoadFromAsset(sequenceToLoad);
      });
    }

    return () => {
      dispatch(InitState());
    };
  }, [id]);

  useEffect(() => {
    dispatch(SetShowSequence(false));
  }, [selectedFileToPreview]);

  const handleModal = () => {
    if (!openModal) return;

    openModal<ModalTimetoolConfigElementProps>(ModalTimetoolConfig, {
      onConfirm: (fps: number, name: string) => {
        dispatch(SetFps(fps));
        dispatch(SetSequenceName(name));
      },
      currentFps: fps,
      sequenceName: sequenceName,
    });
  };

  const handleLoadModal = () => {
    if (!openModal) return;

    openModal<ModalTimetoolLoadSequenceElementProps>(ModalTimetoolLoadSequence, {
      handleLoadFromAsset: handleLoadFromAsset,
      onConfirm: () => {},
    });
  };

  const handleUpdateSequence = () => {
    const sequenceSegmentMetadata = cacheService.GetMetadata().find((x) => x.tagName === "sequenceSegments");
    assetService.Rename({ objectId: loadedSequence!.id, name: sequenceName }).then((res) => {
      assetService
        .UpdateMetadataFields(
          new UpdateMetadataFieldsRequest({
            objectId: loadedSequence!.id,
            metadata: [
              CreateNewDbMetadataValue(
                sequenceSegmentMetadata!.id,
                BaseMetadataType.DbSequenceMetadataValueInfos,
                "sequenceSegments",
                generateMetadata(timelineActions, fps)
              ),
            ],
          })
        )
        .then((response) => {
          if (response.success) enqueueCustomSnackbar(t("alert:success.sequenceSaved"), "success");
          else enqueueCustomSnackbar(response.message ?? t("alert:error.savingSequence"), "error");
        });
    });
  };

  const handleCreateCompilation = () => {
    if (!openModal) return;

    openModal<ModalTimetoolGenerateCompilationElementProps>(ModalTimetoolGenerateCompilation, {
      onGenerated: handleLoadFromSegments,
    });
  };

  const open = Boolean(anchorEl);
  const popoverId = open ? "helper-popover" : undefined;

  const TopBarMemo = useMemo(
    () => (
      <FullTaskTopbar
        title={`${t("page:fullTaskTopbar.timeTool")} - ${t("page:fullTaskTopbar.title.sequenceTimelineVideoEditor")}`}
        backRoute={`${routePaths.home.path}`}
        rightContent={
          <Stack direction="row" display="flex" gap={0.75}>
            <Button
              color="info"
              onClick={(e) => setAnchorEl(e.currentTarget)}
              aria-describedby={popoverId}
              endIcon={<QuestionMarkOutlined />}
              sx={{ zIndex: 1200 }}
            >
              {t("button:help")}
            </Button>
            <Popover
              id={popoverId}
              open={open}
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
            >
              <Stack spacing={1} m={0.75} width={"300px"}>
                <Typography width={"100%"} textAlign={"center"} variant="h6">
                  {t("page:body.shortcuts")}
                </Typography>
                <Stack
                  mt={"12px !important"}
                  height={"24px"}
                  direction={"row"}
                  display={"flex"}
                  justifyContent={"space-between"}
                >
                  <Chip label={`K / ${t("chip:label.spaceBar")}`} />
                  <Typography lineHeight={"24px"} variant="body2">
                    {t("common:play")} / {t("common:pause")}
                  </Typography>
                </Stack>
                <Stack height={"24px"} direction={"row"} display={"flex"} justifyContent={"space-between"}>
                  <Chip label={t("chip:label.holdCtrl")} />
                  <Typography lineHeight={"24px"} variant="body2">
                    {t("page:body.cutMode")} - {t("page:body.cutSegmentOnClick")}
                  </Typography>
                </Stack>
                <Stack height={"24px"} direction={"row"} display={"flex"} justifyContent={"space-between"}>
                  <Chip label=";" />
                  <Typography lineHeight={"24px"} variant="body2">
                    {t("tooltip:title.goToPreviousSegment")}
                  </Typography>
                </Stack>
                <Stack height={"24px"} direction={"row"} display={"flex"} justifyContent={"space-between"}>
                  <Chip label=":" />
                  <Typography lineHeight={"24px"} variant="body2">
                    {t("page:body.goToNextSegment")}
                  </Typography>
                </Stack>
                <Stack height={"24px"} direction={"row"} display={"flex"} justifyContent={"space-between"}>
                  <Chip label="," />
                  <Typography lineHeight={"24px"} variant="body2">
                    {t("page:body.goToSequenceStart")}
                  </Typography>
                </Stack>
                <Stack height={"24px"} direction={"row"} display={"flex"} justifyContent={"space-between"}>
                  <Chip label="!" />
                  <Typography lineHeight={"24px"} variant="body2">
                    {t("page:body.goToSequenceEnd")}
                  </Typography>
                </Stack>
                <Stack height={"24px"} direction={"row"} display={"flex"} justifyContent={"space-between"}>
                  <Chip label="->" />
                  <Typography lineHeight={"24px"} variant="body2">
                    {t("page:body.goToNextFrame")}
                  </Typography>
                </Stack>
                <Stack height={"24px"} direction={"row"} display={"flex"} justifyContent={"space-between"}>
                  <Chip label="<-" />
                  <Typography lineHeight={"24px"} variant="body2">
                    {t("page:body.goToPreviousFrame")}
                  </Typography>
                </Stack>
              </Stack>
            </Popover>
            {userHasCompilationLicense && (
              <Button variant="outlined" color="primary" onClick={handleCreateCompilation}>
                {t("button:createCompilation")}
              </Button>
            )}
            <Button variant="contained" color="primary" onClick={handleLoadModal}>
              {t("common:load")}
            </Button>
            {orderedActionsList.length > 0 && (
              <Button variant="contained" color="primary" onClick={handleModal}>
                {t("button:saveAs")}...
              </Button>
            )}
            {orderedActionsList.length > 0 && loadedSequence !== null && (
              <Button variant="contained" color="primary" onClick={handleUpdateSequence}>
                {t("common:save")}
              </Button>
            )}
          </Stack>
        }
      />
    ),
    [orderedActionsList.length, userHasCompilationLicense, open]
  );

  const FooterMemo = useMemo(
    () => (
      <TimetoolFooter
        sequenceDuration={sequenceDuration}
        fps={fps}
        sequenceName={sequenceName}
        clipCount={orderedActionsList.filter((x) => x?.data?.src !== "").length}
      />
    ),
    [orderedActionsList.length, sequenceName, sequenceDuration, fps]
  );
  return (
    <Stack height="100vh">
      {TopBarMemo}
      <Stack display="flex" direction="column" height="calc(100vh - 100px)" position="relative" top="64px">
        <FullTaskLayout
          sxWrapper={{ flex: 1, maxWidth: "100vw", padding: 0, position: "relative", minHeight: "0px !important" }}
          sxStack={{
            maxWidth: "100vw",
            overflow: "hidden",
            padding: 0,
            position: "relative",
          }}
          withContainer={false}
        >
          {showLibrary && MediaLibraryMemo}
          <Box flex={1} marginTop={0.75} marginRight={0.75} marginBottom={0.75}>
            {showSequence ? <ControlledSequence duration={sequenceDuration} /> : <ControlledPlayer />}
          </Box>
        </FullTaskLayout>
        <FullTaskLayout
          sxWrapper={{ flex: 0.8, minHeight: "0px !important" }}
          sxStack={{ maxWidth: "100vw", overflow: "hidden", position: "relative" }}
          withContainer={false}
        >
          {showTimeline && (
            <Paper
              sx={{
                overflow: "hidden",
                width: "100%",
                boxSizing: "border-box",
                marginRight: 0.75,
                marginLeft: 0.75,
                marginBottom: 0.75,
              }}
            >
              {isLoading ? (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignContent: "center",
                    alignItems: "center",
                    flexDirection: "column",
                  }}
                >
                  <CircularProgress />
                  {loadingMessage && loadingMessage !== "" && (
                    <Typography sx={{ mt: 2 }} color={"text.tier"}>
                      {loadingMessage}
                    </Typography>
                  )}
                </Box>
              ) : (
                <TimelineEditor onGenerated={handleLoadFromSegments} />
              )}
            </Paper>
          )}
        </FullTaskLayout>
      </Stack>
      {FooterMemo}
    </Stack>
  );
};
